import { readCartSnapshot, type CartContext } from "../cart/cart";
import type { ResolvedWidgetConfig } from "../config";

/** 为 Gateway 的 4 秒拉取期限留出往返传输时间；文字路径仍用默认的 3 秒。 */
const VOICE_CART_READ_TIMEOUT_MS = 1_500;

type MessageContextConfig = Pick<
  ResolvedWidgetConfig,
  | "locale"
  | "customerName"
  | "productContext"
  | "storefrontRootUrl"
  | "shopDomain"
  | "playgroundSession"
>;

/** 文字发送与语音读取共用的原始上下文；规范化仍由 message-center 负责。 */
export type MessageContext = {
  locale: string | undefined;
  customer_name: string | undefined;
  page_url: string;
  product?: {
    product_id: string;
    product_title: string;
    product_url: string;
    variant_id: string | undefined;
  };
  cart?: CartContext;
};

/** 保留店面门控和读取预算；读取失败省略购物车，不伪造空车。 */
export function startMessageCartRead(
  config: MessageContextConfig,
  timeoutMs?: number,
): Promise<CartContext | undefined> | undefined {
  if (!config.storefrontRootUrl || config.playgroundSession) return undefined;
  return readCartSnapshot(
    {
      rootUrl: config.storefrontRootUrl,
      shopDomain: config.shopDomain,
    },
    timeoutMs,
  ).then(
    (result) => result.unwrapOr(undefined),
    () => undefined,
  );
}

/** send-message.context 的唯一构建点，保留文字发送的全部字段和值。 */
export function buildMessageContext(
  config: MessageContextConfig,
  product: ResolvedWidgetConfig["productContext"],
  cart: CartContext | undefined,
): MessageContext {
  return {
    locale: config.locale,
    customer_name: config.customerName,
    page_url: location.href,
    ...(product
      ? {
          product: {
            product_id: product.id,
            product_title: product.title,
            product_url: new URL(product.url, location.href).href,
            variant_id: product.variantId,
          },
        }
      : {}),
    ...(cart ? { cart } : {}),
  };
}

export async function readMessageContext(
  config: MessageContextConfig,
): Promise<MessageContext> {
  const cart = await startMessageCartRead(config, VOICE_CART_READ_TIMEOUT_MS);
  // 购物车读取结束后才读商品 getter，反映等待期间的变体切换。
  return buildMessageContext(config, config.productContext, cart);
}
